import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Modal, Textarea, Notification } from '@workboard/wobo-ui';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export type RequestToJoinProps = {
  userId: number;
  teamId: number;
  imgSrc: string;
};

const RequestToJoinModalContent = styled('div')`
  display: flex;
  flex-direction: row;
  height: 300px;
  padding: 20px;
`;
const RequestToJoinModalLeft = styled('div')`
  width: 450px;
`;
const RequestToJoinModalRight = styled('div')`
  width: 300px;
`;
const RequestToJoinModalImage = styled('img')`
  width: 300px;
  height: 300px;
`;
const RequestToJoinModalText = styled('p')`
  font-family: 'Lato';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #000;
`;
const RequestToJoinModalMessage = styled('div')`
  width: 340px;
  textarea {
    width: 316px;
    height: 145px !important;
  }
`;
const RequestToJoinTeamMessageLabel = styled('label')`
  font-family: 'Lato';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.15px;
  color: #596373 !important;
  text-align: left !important;
  margin: 0;
`;
const RequestToJoinModalMessageLimit = styled('span')`
  float: right;
`;

export const RequestToJoinButton = ({
  userId,
  teamId,
  imgSrc,
}: RequestToJoinProps): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [alert, setAlert] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const buttonRef = React.useRef<HTMLInputElement>();
  const handleButtonClick = (): void => {
    setIsOpen(true);
  };
  const handleClose = (): void => {
    setIsOpen(false);
    buttonRef.current.focus();
  };
  const handleNotificationClose = (): void =>
    setAlert({ show: false, type: 'success', message: '' });

  useEffect(() => {
    axios({
      method: 'get',
      url: `${window.wbbaseUrl}/teams/request`,
      params: {
        teamId,
        requestorId: userId,
      },
    }).then((res) => {
      if (res.data.message.type !== 'error') {
        if (res.data.data.length) {
          setHasPendingRequest(true);
        }
      } else {
        setAlert({
          show: true,
          type: 'error',
          message: t('Oops, something went wrong. Please try again later.'),
        });
      }
    });
  }, [teamId, userId]);

  const createUserRequest = useCallback(async () => {
    axios({
      method: 'put',
      url: `${window.wbbaseUrl}/teams/request`,
      data: {
        team_id: teamId,
        requestor_id: userId,
        request_message: message,
      },
    })
      .then((res) => {
        if (res.data.message.type !== 'error') {
          setIsOpen(false);
          setHasPendingRequest(true);
          setAlert({
            show: true,
            type: 'success',
            message: t('Request sent to team manager for approval.'),
          });
        } else {
          setAlert({
            show: true,
            type: 'error',
            message: t('Oops, something went wrong. Please try again later.'),
          });
        }
      })
      .catch(() => {
        setAlert({
          show: true,
          type: 'error',
          message: t('Oops, something went wrong. Please try again later.'),
        });
      });
  }, [message, teamId, userId]);

  const onChange = (e: { target: { value: string } }): void => {
    if (e.target.value) {
      setMessage(e.target.value);
    } else {
      setMessage('');
    }
  };

  return (
    <>
      <Button
        ref={buttonRef}
        kind="normal"
        onClick={handleButtonClick}
        variant="primary"
        isDisabled={hasPendingRequest}
        id="requestToJoinBtn"
      >
        {hasPendingRequest ? t('Request pending') : t('Request to join team')}
      </Button>
      <Modal
        isOpen={isOpen}
        dismissLabel={t('Cancel')}
        onDismiss={handleClose}
        onSubmit={createUserRequest}
        submitLabel={t('Request to join team')}
        title={t('Request to join team')}
      >
        <RequestToJoinModalContent>
          <RequestToJoinModalLeft>
            <RequestToJoinModalText>
              {t(
                "You aren't a member of this team. You can request to join this team and write an optional message to the team manager or admin.",
              )}
            </RequestToJoinModalText>
            <RequestToJoinModalMessage>
              <RequestToJoinTeamMessageLabel htmlFor="wbRequestToJoinModalMessageText">
                {t('Message (optional)')}
                <RequestToJoinModalMessageLimit>
                  {message.length}/280
                </RequestToJoinModalMessageLimit>
              </RequestToJoinTeamMessageLabel>
              <Textarea
                id="wbRequestToJoinModalMessageText"
                autoFocus
                autoComplete="off"
                onChange={onChange}
                maxLength="280"
                placeholder={t(
                  'Write an optional message to the team admin...',
                )}
                value={message}
              />
            </RequestToJoinModalMessage>
          </RequestToJoinModalLeft>
          <RequestToJoinModalRight>
            <RequestToJoinModalImage title="" alt="" src={imgSrc} />
          </RequestToJoinModalRight>
        </RequestToJoinModalContent>
      </Modal>
      <Notification
        open={alert.show}
        severity={alert.type}
        message={alert.message}
        onClose={handleNotificationClose}
      />
    </>
  );
};

export default RequestToJoinButton;
